/**
 * Value formatting utilities for audit log expanded details.
 */

import { formatValue, tryDecodeBase64, formatSecondsToHumanReadable } from '../../utils/formatting';
import { formatCampaignValue, isCampaignMappedField, isDurationSecondsField } from '../../utils/campaign-mapping';
import {
  formatSegmentConditions,
  formatFilters,
  OperatorLabels,
  ConditionTypeLabels,
  TimeDirectionLabels,
  EventTypeLabels,
} from '../../utils/segment-mapping';
import { isEmptyString } from '@/utils/string';
import { CAMPAIGN_OBJECT_TYPES, CONDITION_OBJECT_TYPES } from '../../constants/shared';

// Field names that contain filter/condition structures
const FILTER_FIELD_NAMES = new Set([
  'filters',
  'filter',
  'conditions',
  'trigger',
  'exit_conditions',
  'exitconditions',
  'global_exit_conditions',
  'globalexitconditions',
  'conversion_event_filters',
  'conversioneventfilters',
  'trigger_filters',
  'triggerfilters',
  'entry_filters',
  'entryfilters',
  'event_filters',
  'eventfilters',
  'attribute_filters',
  'attributefilters',
  'recipients', // Newsletter recipients contain segments_negatable filters
  'segment_groups', // Newsletter segment_groups contain segments_negatable filters
  'segmentgroups',
]);

// Field names that contain conversion objects with nested event_name
const CONVERSION_FIELD_NAMES = new Set(['conversion', 'conversionevent', 'conversion_event']);

// Field names that are timestamps where 0 means "never" or "not set"
const TIMESTAMP_FIELDS_WHERE_ZERO_MEANS_EMPTY = new Set([
  'last_used_timestamp',
  'lastusedtimestamp',
  'auto_archived_at',
  'autoarchivedat',
  'scheduled_start',
  'scheduledstart',
  'scheduled_stop',
  'scheduledstop',
  'deleted_at',
  'deletedat',
  'archived_at',
  'archivedat',
]);

/**
 * Check if a value looks like a filter/condition object (has and/or/not structure or filter properties).
 * Also recognizes arrays of filter objects (e.g., segment_groups for newsletters).
 */
function isFilterObject(value: unknown): boolean {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  // Handle arrays of filter objects (e.g., segment_groups: [{ type: 'segments_negatable', ... }])
  if (Array.isArray(value)) {
    // Check if it's a non-empty array where items look like filter objects
    if (value.length > 0) {
      return value.every((item) => {
        if (item == null || typeof item !== 'object' || Array.isArray(item)) {
          return false;
        }
        const itemObject = item as Record<string, unknown>;
        // Check for known condition types used in newsletters and campaigns
        // Types from ~/code/ui/app/utils/condition-types.ts
        const knownConditionTypes = [
          'segments_negatable',
          'segment',
          'attribute',
          'event_attribute',
          'message', // Message interaction conditions
          'event',
          'pageview',
          'screenview',
          'form',
          'device_attribute',
          'newsletter_recipients',
          'inbound',
          'opt_out',
          'relationship',
          'object_attribute',
        ];
        const itemType = itemObject.type as string | undefined;
        return (
          (itemType != null && knownConditionTypes.includes(itemType)) ||
          'and' in itemObject ||
          'or' in itemObject ||
          'segment_id' in itemObject ||
          'event' in itemObject || // Message conditions have { event: {...} }
          ('field' in itemObject && 'operator' in itemObject)
        );
      });
    }
    return false;
  }

  const object = value as Record<string, unknown>;
  return (
    'and' in object ||
    'or' in object ||
    'not' in object ||
    'segment' in object ||
    'segment_id' in object || // Segment conditions format
    'condition_id' in object || // Condition reference format
    'event_name' in object ||
    'event' in object || // Message conditions
    object.type === 'segments_negatable' || // Newsletter recipients format
    object.type === 'message' || // Message interaction conditions
    object.type === 'attribute' ||
    ('field' in object && 'operator' in object) ||
    ('attribute' in object && 'operator' in object)
  );
}

/**
 * Check if a value looks like a conversion object with nested event_name.
 * E.g., { event_name: { etype: "", name: "purchase" } }
 */
function isConversionObject(value: unknown): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const object = value as Record<string, unknown>;
  return 'event_name' in object && typeof object.event_name === 'object';
}

/**
 * Format a conversion object that contains an event_name.
 */
function formatConversionObject(value: unknown): string {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return formatValue(value);
  }
  const object = value as Record<string, unknown>;
  if ('event_name' in object && typeof object.event_name === 'object' && object.event_name != null) {
    const eventName = object.event_name as Record<string, unknown>;
    const name = eventName.name as string | undefined;
    const etype = eventName.etype as string | undefined;

    if (name != null) {
      const displayName = tryDecodeBase64(name);

      if (!isEmptyString(etype)) {
        return `Event "${displayName}" (${etype})`;
      }
      return `Event "${displayName}"`;
    }
  }
  return formatValue(value);
}

/**
 * Format a value for display, using:
 * - Campaign-specific mappings for Campaign/CampaignAction objects
 * - Condition formatting for Segment objects (conditions field)
 * - Filter formatting for filter fields in any object type
 * - Operator/type/inverse/timeDirection labels for Condition objects
 *
 * @param value - The raw value to format
 * @param fieldName - The field name (used for context-specific mapping)
 * @param objectType - The object type from the audit log
 * @returns Formatted string for display
 */
export const formatDisplayValue = (value: unknown, fieldName: string, objectType: string): string => {
  const lowerFieldName = fieldName.toLowerCase().replace(/[_-]/g, '');

  // For timestamp fields where 0 means "never" or "not set", show em dash
  if (value === 0 && TIMESTAMP_FIELDS_WHERE_ZERO_MEANS_EMPTY.has(lowerFieldName)) {
    return '—';
  }

  // For Segment objects, format conditions field using segment builder formatting
  if (objectType === 'Segment' && fieldName === 'conditions') {
    return formatSegmentConditions(value);
  }

  // For conversion fields that contain nested event_name
  if (CONVERSION_FIELD_NAMES.has(lowerFieldName) && isConversionObject(value)) {
    return formatConversionObject(value);
  }

  // For filter/condition fields, format using the filter formatter
  if (FILTER_FIELD_NAMES.has(lowerFieldName) && isFilterObject(value)) {
    return formatFilters(value);
  }

  // For Condition objects, format various fields with semantic labels
  if (CONDITION_OBJECT_TYPES.has(objectType)) {
    // Format operator field using OperatorLabels
    if (fieldName === 'operator' && typeof value === 'string') {
      return OperatorLabels[value] ?? value;
    }

    // Format type field using ConditionTypeLabels
    if (fieldName === 'type' && typeof value === 'string') {
      return ConditionTypeLabels[value] ?? value;
    }

    // Format inverse field with semantic meaning
    if (fieldName === 'inverse' && typeof value === 'boolean') {
      return value ? 'Yes (condition is negated)' : 'No';
    }

    // Format timeDirection field (used with relative timestamp operators)
    if ((fieldName === 'timeDirection' || fieldName === 'time_direction') && typeof value === 'string') {
      return TimeDirectionLabels[value] ?? value;
    }

    // Format eventType / event_type field using EventTypeLabels (message event types)
    if ((fieldName === 'eventType' || fieldName === 'event_type') && typeof value === 'string') {
      return EventTypeLabels[value] ?? value;
    }
  }

  // For Campaign and CampaignAction objects, try campaign-specific formatting first
  if (CAMPAIGN_OBJECT_TYPES.has(objectType)) {
    // Format duration fields (delay, debounce_secs, etc.) as human-readable durations
    // e.g., 691200 seconds → "8 days"
    if (isDurationSecondsField(fieldName) && typeof value === 'number' && value > 0) {
      return formatSecondsToHumanReadable(value);
    }

    // Check if this field has a campaign-specific mapping
    if (isCampaignMappedField(fieldName)) {
      return formatCampaignValue(fieldName, value, objectType);
    }
    // For boolean fields in campaign context, use semantic labels
    if (typeof value === 'boolean') {
      return formatCampaignValue(fieldName, value, objectType);
    }
  }

  // Fall back to generic formatting
  return formatValue(value);
};
